/**
 * Prop Studio — every piece of English the page shows.
 *
 * The studio is a dev / super-admin tool while the photoreal library is being
 * made, so these are not in the locale dictionaries (as the designer's are
 * not, see designer/strings.ts). One place, so a later move into
 * public/locales is a mechanical one.
 */
import { DESIGNER_STRINGS } from '../designer/strings';

export const PROP_STUDIO_STRINGS = {
  title: 'Prop Studio',
  subtitle: 'Photoreal stand-ins for the staged props',
  designer: '3D Designer',
  backToEstimator: 'Estimator',

  // Left column
  spaceType: DESIGNER_STRINGS.spaceType,
  spaceTypeName: DESIGNER_STRINGS.spaceTypeName,
  props: 'Props',
  kindTag: { slatwall: 'hung', wall: 'on the wall', ceiling: 'ceiling', yard: 'in the yard' } as Record<string, string>,
  offTag: 'off',
  offTagTitle: 'Out of the staging pool (Prop Studio’s In the staging pool box)',
  allProps: 'All props',
  fixtures: 'Fixtures',
  madeTag: 'made here',

  // The page's sections (header)
  sections: { props: 'Props', finishes: 'Floors & lighting', looks: 'Look Lab' } as Record<'props' | 'finishes' | 'looks', string>,
  sectionsTitle: {
    props: 'Place a photoreal model for each staged prop',
    finishes: 'The floors and lighting each kind of space is rendered with',
    looks: 'Make and manage the render looks every customer’s render of a space is given',
  } as Record<'props' | 'finishes' | 'looks', string>,

  // The Look Lab (props/LookLab.tsx; renderLooks.ts)
  lookLab: {
    room: 'Room',
    roomTitle: 'The room to try looks on: a preset, or a saved estimate’s own room',
    estimate: 'Estimate',
    estimatePlaceholder: 'Estimate link or id',
    openEstimate: 'Open',
    estimateLoading: 'Opening the estimate…',
    estimateNotDesigned: 'That estimate was not made in the 3D designer: it has no room to open.',
    estimateNotFound: (why: string) => `Could not open that estimate (${why}).`,
    estimateRoom: (name: string) => `Estimate: ${name}`,
    space: 'Space',
    spaceTitle: 'The kind of space these looks are for: every customer render of one picks one of its enabled looks at random',
    looksOf: (space: string) => `${space} looks`,
    none: (space: string) => `No looks for ${space.toLowerCase()} yet: its renders use the factory settings. Tune the panel on the right, then Save as new look.`,
    intro: 'Each customer render of this space gets one of the enabled looks, picked by the estimate — their room, their props, this lighting and these finishes.',
    on: 'On',
    off: 'Off',
    onTitle: 'Picked for customer renders',
    offTitle: 'Kept, but never picked for a customer render',
    editing: 'Editing',
    edit: 'Edit',
    editTitle: 'Load this look into the panel to change it',
    duplicate: 'Duplicate',
    rename: 'Rename',
    renamePrompt: 'New name for this look:',
    remove: 'Delete',
    removeConfirm: (name: string) => `Delete the look “${name}”? Renders already made keep it; new ones will not get it.`,
    saved: (when: string) => `saved ${when}`,
    panelTitle: 'Look settings — what Blender renders',
    panelNew: 'New look (not saved)',
    panelEditing: (name: string) => `Editing “${name}”`,
    unsaved: 'unsaved changes',
    saveNew: 'Save as new look',
    saveNewPrompt: (space: string) => `Name of the new ${space.toLowerCase()} look:`,
    update: (name: string) => `Save “${name}”`,
    startOver: 'New look',
    startOverTitle: 'Start a new look from the factory settings',
    full: (max: number) => `This space holds ${max} looks at most.`,
    discardConfirm: 'The panel has unsaved changes. Leave them?',
    saving: 'Saving…',
    savedNote: 'Saved.',
    testQueued: (pos: number) => `Test render queued (${pos === 1 ? 'next' : `#${pos} in line`}). Pictures appear below as they land.`,
    testNotReady: 'The room is not ready to render yet.',
    testTitle: 'Test render',
    testStatus: (status: string, percent: number) => `${status} · ${percent}%`,
    testViews: 'Test render',
    testViewsHint: 'What a test render makes: the view you are looking through (about a minute), or the whole set a customer gets',
    testViewUser: 'This view',
    testViewAll: 'All views + 360',
    testWorker: (worker: string) => `on ${worker.startsWith('runpod-') ? `the cloud (${worker.slice(7)})` : worker}`,
    usePicture: 'Use as the look’s picture',
    usePictureTitle: 'Show this picture on the look’s card',
    noBackEnd: 'The props back end is not answering, so looks cannot be loaded or saved here.',
    // The sidebars fold away to give the 3D view the width.
    hideLeft: 'Hide the room and looks panel',
    showLeft: 'Show the room and looks panel',
    hideRight: 'Hide the look settings',
    copyEffects: 'Copy effects to every look',
    copyEffectsTitle: 'Put this panel’s Effects tab — exposure, colour handling, contrast, saturation, contact shading, bloom, vignette, colour fringing, softness, film grain — on every saved look of every space. Their lighting, camera, materials and textures stay as they are.',
    copyEffectsConfirm: (n: number) => `Copy these effects onto all ${n} saved looks, in every space? Each look keeps its own lighting, camera, materials and textures.`,
    copyEffectsNone: 'No saved looks to copy to.',
    copiedEffects: (n: number) => `Effects copied to ${n} looks.`,
    showRight: 'Show the look settings',
    leftTab: 'Room & looks',
    rightTab: 'Look settings',
  },

  // Floors & lighting by space (props/FinishesManager.tsx; renderFinish.ts)
  finishes: {
    spaces: 'Spaces',
    count: (floors: number, hdri: number) => `${floors} floor${floors === 1 ? '' : 's'} · ${hdri} lighting`,
    intro: (space: string) => `When a ${space.toLowerCase()} is staged, it is rendered with one of these floors and one of these lights, drawn at random — each room keeps its draw. Changes save as you make them; stage a room again to pick them up.`,
    introOther: 'When a room of this kind is staged, it is rendered with one of these floors and one of these lights, drawn at random — each room keeps its draw. Changes save as you make them; stage a room again to pick them up.',
    floors: 'Floors',
    floorsNone: 'No floors yet: rooms of this kind render with the built-in sealed concrete. Add one from the library below.',
    lighting: 'Lighting (HDRI)',
    lightingNone: 'No lighting yet: rooms of this kind render with the plain sky.',
    tune: 'Tune',
    tuneHint: 'Scale, turn, roughness, relief and brightness — seen live on a floor in your browser',
    tuner: {
      title: (set: string, space: string) => `Tune ${set} · ${space}`,
      light: 'Lit by',
      lightHint: "One of this space's lights (its strength and turn), or a neutral studio light",
      studio: 'Studio light',
      save: 'Save to this space',
      cancel: 'Cancel',
      drag: 'Drag to look around; scroll to come closer.',
      note: "A live preview in the browser, not the render: judge the tile's scale, pattern and tone here. The render's light and sheen are richer.",
    },
    findTitleFloors: (space: string) => `Find floors for ${space}`,
    findTitleLighting: (space: string) => `Find lighting for ${space}`,
    findFloorsNote: 'Click a result to download it: it joins the library and this space.',
    findLightingNote: 'Click a result to download it (or upload your own): it joins the library and this space.',
    remove: 'Remove from this space',
    missing: 'not in the texture library',
    missingHdri: 'not in the HDRI library',
    strength: 'Strength',
    rotation: 'Turn',
    addFloor: 'Add a floor from the library',
    addFloorHint: 'Click a set to add it to this space. Already in: marked.',
    addLighting: 'Add lighting from the library',
    libraryEmpty: 'The library has none yet — find some below.',
    findTextures: 'Find more floors',
    findHdri: 'Find more lighting',
    inSpace: 'in this space',
    full: (max: number) => `A space holds up to ${max}.`,
    saving: 'Saving…',
    saved: 'Saved to the library',
    loading: 'Loading the library…',
  },

  // Where a prop can go, its spaces, props made here (staging/catalogue.ts)
  catalogue: {
    inPool: 'In the staging pool',
    inPoolTitle: 'Off: staging leaves this prop out of every space, but keeps its spaces, places, box and models. Tick it to bring it back.',
    title: 'Where it can go',
    hint: 'Staging tries these best first: what was ticked first stays first; a new hook or shelf goes after the others, the floor or the ceiling last. Boots on a shelf, else on the floor. On the wall is on its own: held at its height on the wall’s product, off the openings (wall art, a towel bar).',
    placeNames: {
      shelf: 'Shelf', basket: 'Basket', 'hook-single': 'Single hook', 'hook-double': 'Double hook',
      'hook-bike': 'Bike hook', 'hook-hose': 'Hose hook', wall: 'On the wall', floor: 'Floor', ceiling: 'Ceiling', yard: 'Yard',
    } as Record<string, string>,
    wallPlace: 'On the wall: held at its height (Held at, below), its bottom there unless it hangs below the hold point. It goes nowhere else.',
    order: (list: string) => `Tried in this order: ${list}`,
    previewAs: 'Preview as',
    previewAsTitle: 'Which way the prop is shown placed here (the model and its placement are the same for all of them; the hang point is used on hooks and the ceiling, the bottom on the floor, a shelf or in a basket)',
    spaces: 'Staged in',
    spacesHint: 'The spaces whose staging includes it.',
    noSpace: 'In no space: staging leaves it out (find it under All props).',
    wallProp: 'A built-in wall prop (it is fixed to the wall at its height): it goes nowhere else.',
    name: 'Name',
    size: 'Size (ft)',
    sizeTitle: 'Along the wall, out from it, and up: what staging keeps room for before a model is placed (the model’s own size takes over once it is).',
    matchModel: 'Match the model',
    matchModelTitle: 'Set the box to the model as it is placed here: its width along the wall, how far it comes out, and its height from the floor. Saved at once.',
    matchModelHeldTitle: 'Move the model onto the gold ball (its bottom there, or its top when it hangs; centred along the wall and in depth), then set the box to the model’s size, its back on the wall. Saves the placement and the box.',
    matched: 'Box and model matched: staging uses it from the next Stage.',
    modelIs: (w: number, d: number, h: number) => `model ${w} × ${d} × ${h} ft`,
    drop: 'Hangs below a hook (ft)',
    dropTitle: 'On a hook, how far it hangs below the point the hook holds it at (blank: most of its height).',
    boxTitle: 'Box and hold point',
    boxHint: 'The box staging keeps room for (and draws the stand-in in), where the prop is held, and what it keeps clear. Saved at once; the next Stage uses it.',
    along: 'along',
    deep: 'deep',
    tall: 'tall',
    clearance: 'Keep clear (ft)',
    clearanceTitle: 'The room kept free round the box: in front (to use it), either side (off other props, wall ends and corners), and off the wall behind.',
    clearFront: 'front',
    clearSides: 'sides',
    clearBack: 'off wall',
    mountHeight: 'Held at (ft up)',
    mountHeightTitle: 'Wall props: how high above the floor the prop is held (the bar of a towel bar).',
    hold: 'Hold point (ft)',
    holdTitle: 'Where the gold ball is: along the wall from the box’s centre (+ right), how far the box hangs below it, and out from the wall.',
    holdAlong: 'along',
    holdBelow: 'hangs below',
    holdOut: 'out',
    count: 'How many',
    countTitle: 'How many of it a room gets. The Stage dialog’s density thins or multiplies this unless it is fixed.',
    fixed: 'Fixed: this many, however sparse or dense the staging',
    stands: 'Stands',
    standsTitle: 'Where a floor prop goes: with its back to a wall, out in the room (facing its middle), either, as the seed decides, or in a corner only — tight to both walls, its back to one and its left side to the other (a shower). With no corner free it is left out.',
    standsWall: 'Against a wall',
    standsFree: 'Out in the room',
    standsAny: 'Either',
    standsCorner: 'In a corner',
    standsBay: 'In line with a garage door',
    placed: 'Placed',
    placedTitle: 'When staging places it, among the space’s props: first takes the best spot (a shower gets its corner before the toilet does). First and the main pieces are always placed once, however sparse the staging; the rest are thinned or multiplied by the density.',
    placedFirst: 'First',
    placedMain: 'With the main pieces',
    placedRest: 'With the rest',
    placedLast: 'Last',
    placedOrder: (n: number) => `order ${n}`,
    standsLawn: 'Out on the lawn',
    standsWindow: 'Under a window',
    standsYardTitle: 'Where a yard prop goes: out on the lawn, clear of the house and the driveway, where the windows look; or under an exterior window it fits beneath, a step off the wall.',
    heading: 'Nose',
    headingTitle: 'Which way it faces in the bay: into the room (its back to the door), out through the door, or either, as the seed decides.',
    headingAny: 'Either way',
    headingIn: 'Into the room',
    headingOut: 'Out through the door',
    inside: 'In the room',
    insideTitle: 'How much of its length is in the room. Half or a quarter: the rest stands outside through the door, and the door is held open. On the driveway: all of it outside the closed door, on the apron.',
    insideAll: 'All the way',
    insideHalf: 'Half',
    insideQuarter: 'A quarter',
    insideNone: 'On the driveway',
    beside: 'Beside',
    besideTitle: 'Stands right beside this prop on the same wall, whenever that fits (a dryer by the washer, a fryer by the range).',
    besideNone: 'Nothing',
    besideGap: 'gap (ft)',
    askew: 'Any angle',
    askewTitle: 'Out in the room: may stand at any angle, facing the middle (a person). Off: square to the walls, as furniture stands.',
    faces: 'Faces',
    facesTitle: 'What it faces: the room — or stood in front of this prop, facing it, when that prop is in the room (a woman at the vanity, a sofa across from the TV console). It is placed right after that prop, whatever its order.',
    facesRoom: 'The room',
    facesGap: 'in front by (ft)',
    facesGapTitle: 'How far in front of that prop it stands, its front to the other’s front: a hand’s gap for a person at a vanity, a viewing distance for a sofa. A room too small for it brings it closer. The floor between them is kept clear.',
    centred: 'Centred on its wall',
    centredTitle: 'Against a wall: at the middle of the wall only, never elsewhere along it (a TV console). Each wall is tried in the seed’s order.',
    saving: 'Saving…',
    saved: 'Saved: staging uses it from the next Stage.',
    newProp: '+ New prop',
    newPropTitle: 'Make a prop of your own: give it a name, then a model, and say where it can go',
    newPropPrompt: 'Name of the new prop (a kayak, a hanging plant…):',
    made: (label: string) => `${label} is made: give it a model, place it, and say where it can go.`,
    saveFirst: 'Save (or Reset) this placement first.',
    remove: 'Delete this prop',
    removeConfirm: (label: string) => `Delete "${label}"? Staging stops using it. (Its models stay in the library until removed there.)`,
    removed: (label: string) => `${label} is deleted.`,
    fixtureNote: 'A fixture: the garage door’s hardware hangs it where the motor goes (its top-centre at the motor box’s top, its front toward the door). Size, turn it and nudge it; without a model the renderer builds its own.',
  },
  kindTagTitle: 'Held on the wall: the model hangs from (or sits at) the gold ball, where the accessory holds it.',
  status: { none: 'none', assigned: 'assigned', stale: 'stale' } as Record<'none' | 'assigned' | 'stale', string>,
  statusTitle: {
    none: 'No model yet',
    assigned: 'A model is assigned and its preview is current',
    stale: 'The model changed since its preview was made — Reconvert',
  } as Record<'none' | 'assigned' | 'stale', string>,
  libraryOffline: 'Library not loaded',

  // The hang point: the spot on a held prop's model that goes on the hook
  hang: {
    title: 'Hang point',
    boxTop: 'The box’s top-centre',
    boxBottom: 'The box’s bottom-centre',
    picked: (x: string, y: string, z: string) => `Picked on the model (${x}, ${y}, ${z} cm in its own frame)`,
    pick: 'Pick on the model',
    pickTitle: 'Then click the spot on the model that should sit on the hook — a grip, a hole, the frame. The view still orbits with a drag; Esc cancels.',
    picking: 'Click the model where it hangs… (Esc cancels)',
    cancel: 'Cancel',
    clear: 'Back to the box',
    clearTitle: 'Hang it from the box’s top-centre (or bottom-centre) again',
    note: 'The cyan ring is the hang point: it sits on the gold ball, and Offset moves the model from there.',
    heldPicked: 'Held prop: the hang point you picked on the model sits on the gold ball (the hook). Offset moves it from there.',
    picked2: (x: string, y: string, z: string) => `Set at (${x}, ${y}, ${z} cm in the model’s own frame)`,
    axes: [
      ['Across', 'Left to right across the model’s box, in its own frame (0% one side, 100% the other)'],
      ['Depth', 'Back to front through the model’s box, in its own frame'],
      ['Height', 'Bottom to top of the model’s box, in its own frame'],
    ] as [string, string][],
    percentTitle: 'Where the hang point is in the model’s box: 0–100% is inside it; it can go a little past (−25% to 125% on the slider, further typed) — between two models, say',
    slidersNote: 'Pick a spot on the model, or set it here — the sliders move it within (or just past) the model’s box.',
  },

  // Parts: models on a model
  parts: {
    title: (model: string) => `Parts on ${model}`,
    hint: 'Models placed on this one — a toolbox on the bench — in its frame: Offset Up by the bench top’s height to set it on the bench. Rendered with it, copied with it.',
    one: (n: number) => `Part ${n}`,
    add: '+ Add a part',
    addTitle: 'Put another model on this one: assign it, place it in this model’s frame, save',
    note: (owner: string) => `Part of ${owner}: placed in that model’s frame (its origin, its turn). The wall and the kit box are the prop’s.`,
  },

  // Variants: more than one model for the same prop
  variants: {
    title: 'Models for this prop',
    hint: 'A render picks one of them for each staged copy (the same seed picks the same). The designer stages the prop round the largest.',
    one: (n: number) => `Model ${n}`,
    add: '+ Add another',
    addTitle: 'Another model for this prop: assign it, place it, save',
    fresh: 'New — assign a model below',
    spaces: (n: number) => `Model ${n} staged in`,
    spacesHint: 'Which of the prop’s spaces THIS model is staged in: a steel shelf for the garage and shed, an oak one for the basement. Staging picks a copy’s model among those for the room’s space; with none for it, the copy is left out there. Saved at once.',
    spacesAll: 'every space the prop is in',
    spacesNone: 'nowhere: this model is never staged',
  },

  // Viewer
  viewer: {
    sideBySide: 'Side by side',
    overlay: 'Overlay',
    sideBySideTitle: 'The candidate beside the procedural prop',
    centreModel: 'Centre on model',
    centreModelTitle: 'Bring the camera onto the model alone, filling the view — to place it on its shelf or hook',
    centreAll: 'Show all',
    centreAllTitle: 'The camera back on the whole arrangement',
    overlayTitle: 'The candidate over the procedural prop (ghosted), the kit box drawn on top',
    roomArrow: 'room',
    markersOn: 'Markers: on',
    markersOff: 'Markers: off',
    markersTitle: 'The hold point (gold ball) and the hang point (blue ring) drawn, or hidden to place the model unobstructed. Picking a hang point still works with them off.',
    hint: 'Drag to orbit · Scroll to zoom · Click the view, then arrow keys nudge the model 0.1 ft (Shift 1 ft)',
    wallHint: 'The pale plane is the wall the prop stands against; nothing should pass behind it.',
    colourNote: 'Preview colours can differ from the render — the render uses the .blend’s own materials.',
    noCandidate: 'No model for this prop yet — pick one on the right.',
    noPart: (n: number) => `Nothing assigned to Part ${n} yet — pick a model on the right; it will be placed on the model shown.`,
    noPreview: 'The model has no preview (.glb) yet — Reconvert.',
    loading: 'Loading the model…',
    loadFailed: (why: string) => `The model could not be loaded: ${why}`,
    noWebgl: 'This browser could not start WebGL.',
  },

  // Right column: the candidate
  candidate: {
    title: 'Candidate',
    current: 'Current model',
    none: 'Nothing assigned to this prop.',
    nonePart: 'Nothing assigned to this part yet — search, paste an id, or upload a file.',
    source: { blenderkit: 'BlenderKit', local: 'Your file' } as Record<string, string>,
    licence: 'Licence',
    author: 'By',
    page: 'Open on BlenderKit',
    files: 'Files',
    renderer: 'renderer',
    preview: 'preview',
    path: 'Model file',
    copyPath: 'Copy path',
    copied: 'Copied',
    openHint: 'Open it in Blender, save, then Reconvert.',
    stale: 'The preview is older than the model. Reconvert to see what the renderer will get.',
    edited: 'Edited in Blender since it was downloaded: this file is the only copy of those edits.',
    reconvert: 'Reconvert',
    reconverting: 'Converting in Blender…',
    remove: 'Remove this model',
    removeConfirm: (label: string) => `Remove the model from ${label}? Its files are deleted; the prop goes back to the procedural one.`,
    replaceConfirm: (current: string, next: string) =>
      `Replace ${current} with ${next}?\n\nThe current model files are deleted. A BlenderKit model can be fetched again by its asset id (the notice afterwards names it); there is no undo on this page.`,
    replaceConfirmStrong: (current: string, next: string, why: string) =>
      `Replace ${current} with ${next}?\n\n${why} Its files are deleted and there is no other copy — this cannot be undone.`,
    replaceWhyLocal: 'This model is a file of your own.',
    replaceWhyEdited: 'This model was edited in Blender after it was downloaded.',
    replaceWhyStale: 'This model has changed since its preview was made (the edits may not be anywhere else).',
    saved: (when: string) => `Saved ${when}`,
    previewSize: 'Preview file',
    previewDiffers: 'The preview’s size disagrees with the recorded bounds — Reconvert.',
  },
  licenceName: { cc_zero: 'CC0', royalty_free: 'Royalty-free' } as Record<string, string>,

  search: {
    title: 'Find a model on BlenderKit',
    placeholder: 'Search models — “bicycle”, “workbench”…',
    freeOnly: 'Free only',
    go: 'Search',
    searching: 'Searching…',
    none: 'Nothing found.',
    more: 'More results',
    close: 'Close',
    resultsFor: (label: string) => `These go to ${label}`,
    resolution: 'Textures',
    resolutionHint: 'for “Use this model” and a pasted asset id alike',
    resolutionNames: {
      resolution_0_5K: '0.5K', resolution_1K: '1K', resolution_2K: '2K', resolution_4K: '4K', blend: 'Original file',
    } as Record<string, string>,
    originalWarning: 'The original file is the full .blend with every texture at full size — often 100–300 MB (each result shows its size). 1K or 2K is usually plenty for a render.',
    use: 'Use this model',
    busy: 'Downloading and converting — this can take a minute…',
    assigned: (name: string) => `${name} is assigned. Place it, then Save.`,
    assignedReplacing: (name: string, previous: string, asset: string | undefined) =>
      `${name} is assigned, replacing ${previous}${asset ? ` (asset ${asset} — paste the id to get it back)` : ''}. Place it, then Save.`,
    cannotDownload: 'Not downloadable with this account.',
    paid: 'Paid — needs the account’s API key on the helper.',
    size: (mb: string) => `${mb} MB`,
    originalSize: (mb: string) => `original ${mb} MB`,
    dims: (x: string, y: string, z: string) => `${x} × ${y} × ${z} m`,
  },
  assetId: {
    label: 'Or paste an asset id',
    placeholder: 'BlenderKit asset id, or the address of its page',
    use: 'Use',
    extracted: (id: string) => `Asset id: ${id}`,
    none: 'No asset id in that — one looks like 32783d96-735c-4365-9bf4-818b9fc39727.',
  },
  upload: {
    label: 'Or upload a model of your own',
    formats: '.blend, .glb or .gltf as they are; .stl, .obj, .fbx and .ply are converted (an STL gets a plain grey finish).',
    units: 'Units',
    unitsTitle: 'What one unit in the file is. An STL, OBJ or PLY has no units of its own: Auto takes one over 20 units across to be in millimetres (as CAD and 3D-printing files are), else metres. A .blend, .glb or .fbx says its own.',
    unitNames: { auto: 'Auto', mm: 'mm', cm: 'cm', m: 'm', in: 'inches', ft: 'feet' } as Record<string, string>,
    busy: 'Uploading and converting…',
    done: (name: string) => `${name} is assigned. Place it, then Save.`,
    doneReplacing: (name: string, previous: string, asset: string | undefined) =>
      `${name} is assigned, replacing ${previous}${asset ? ` (asset ${asset} — paste the id to get it back)` : ''}. Place it, then Save.`,
    badType: 'That is not a model file the helper takes: .blend, .glb, .gltf, .stl, .obj, .fbx or .ply.',
  },

  // Placement controls
  placement: {
    title: 'Placement',
    scale: 'Scale',
    yaw: 'Turn',
    yawTitle: 'Degrees about the up axis; positive is counter-clockwise seen from above',
    minus90: '−90°',
    plus90: '+90°',
    zero: '0°',
    offset: 'Offset (ft)',
    along: 'Along wall',
    up: 'Up',
    into: 'Into room',
    mirror: 'Mirror',
    mirrorTitle: 'Flip the model left to right',
    stand: 'Stand',
    standTitle: 'Stand the model up before it is turned: a model built lying on its back takes a tip of 90°',
    tilt: 'Tip',
    tiltTitle: 'Degrees about the model’s own left–right axis (forward and back)',
    roll: 'Roll',
    rollTitle: 'Degrees about the model’s own front–back axis (sideways)',
    copies: 'Copies',
    copiesTitle: 'Repeat the model: each copy starts where the one before ends, plus the gap — two crates make a stack',
    copyCount: 'Count',
    copyAxis: 'Run',
    copyUp: 'stacked up',
    copyAlong: 'along the wall',
    copyInto: 'into the room',
    copyGap: 'Gap (ft)',
    perWall: 'Per wall',
    perWallTitle: 'At most this many of THIS model on one wall (a hung prop) or in one room (a floor prop). 0: no limit. The prop’s other models may still go beside it; a copy with no model left under its limit is left out.',
    perWallOn: (n: number) => `at most ${n} on a wall / in a room`,
    perWallOff: 'no limit',
    fit: 'Fit',
    fitAt: (yaw: number) => `at ${yaw}°`,
    fitAtTitle: 'The fits are worked out for the model as turned now; turn it first, then fit.',
    fitFootprint: 'footprint',
    fitHeight: 'height',
    fitBox: 'box',
    fitFootprintTitle: 'Scale so the model fills the kit box’s floor plan',
    fitHeightTitle: 'Scale so the model is as tall as the kit box',
    fitBoxTitle: 'Scale so the model fits inside the kit box every way',
    stageBox: 'Stage as',
    stageBoxTitle: 'The box the designer stages and draws this model at. Off: the placed size (copies and parts in). On: a size of your own — a vanity whose model carries a mirror is staged as its cabinet, not the whole.',
    stageBoxOwn: 'a size of my own',
    stageBoxFromPlaced: 'From placed',
    stageBoxFromKit: 'From kit box',
    stageBoxNote: 'The render still shows the whole model; only the designer’s stand-in and its clearances change.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved.',
    reset: 'Reset to saved',
    unsaved: 'Unsaved changes',
    noModel: 'Assign a model to place it.',
    heldTop: (holdZ: string) => `Held prop: the model’s top-centre hangs from the gold ball (the hook’s prong, ${holdZ} ft out from the wall). Offset “Up” to lower it on the hook.`,
    heldBottom: (holdZ: string) => `Held prop: the model’s bottom-centre sits at the gold ball (the shelf’s top or basket’s floor, ${holdZ} ft out from the wall).`,
    unsavedTitle: 'Unsaved placement',
    unsavedBody: (label: string, next: string) => `${label} has placement changes that are not saved. Save them before going to ${next}?`,
    saveAndGo: 'Save and switch',
    discard: 'Discard changes',
    stay: 'Stay here',
    leaveConfirm: 'The placement has unsaved changes. Leave the page and lose them?',
  },
  readout: {
    title: 'Size',
    stagingNote: 'While the helper is running, the designer stages each copy of this prop for one of its models, at that model’s staged size, and draws the model’s preview in its place; the render shows the same one.',
    model: 'Model',
    placed: 'Placed',
    staged: 'Staged',
    kitBox: 'Kit box',
    difference: 'Difference',
    along: 'along',
    deep: 'deep',
    tall: 'tall',
    metres: 'm',
    feet: 'ft',
    bottom: 'Bottom',
    centre: 'Centre',
    behindWall: (cm: string, ft: string) => `${cm} cm (${ft} ft) behind the wall`,
    rest: 'Rest on floor',
    restTitle: 'Move the model so its lowest point — the mesh as drawn, tipped or rolled — touches the floor (changes the “Up” offset)',
    flush: 'Flush to wall',
    flushTitle: 'Move the model so its nearest point sits exactly on the wall line (changes the “Into room” offset)',
    clearOfWall: (cm: string, ft: string) => `Clear of the wall by ${cm} cm (${ft} ft)`,
  },

  notice: {
    serviceDown: 'Start the helper: node scripts/props/server.js',
    serviceDownPhp: 'This site did not answer Prop Studio (api/props.php) — is the local site running?',
    helperOutOfDate: 'The helper that is running is from before a code change and will refuse what this page sends — restart it: Ctrl+C in its terminal, then node scripts/props/server.js',
    helperOutOfDatePhp: 'This site’s api/props.php is from a different build than this page — rebuild and deploy locally, then reload.',
    originRefused: (origin: string) => `The helper refused this page’s origin (${origin}); open the page as http://localhost:3000 or set PROPS_ORIGINS`,
    loadingLibrary: 'Loading the library…',
    removed: (label: string) => `${label} is back to the procedural prop.`,
  },
} as const;
